import type { LoggerConfig } from './logger.service';

export type LogLevelName = 'trace' | 'debug' | 'info' | 'warn' | 'error' | 'fatal';

const LOG_LEVEL_ORDER: readonly LogLevelName[] = [
    'trace',
    'debug',
    'info',
    'warn',
    'error',
    'fatal',
];

export function parseLogLevel(value: string | undefined, fallback: LogLevelName = 'error'): LogLevelName {
    const normalized = value?.trim().toLowerCase();
    if (!normalized) {
        return fallback;
    }

    if ((LOG_LEVEL_ORDER as readonly string[]).includes(normalized)) {
        return normalized as LogLevelName;
    }

    return fallback;
}

/** Reads `LOG_LEVEL` (Node) or `VITE_LOG_LEVEL` (Vite client build). Defaults to `error`. */
export function readLogLevelFromEnv(): LogLevelName {
    if (typeof process !== 'undefined' && process.env) {
        const fromProcess = process.env['LOG_LEVEL'];
        if (fromProcess !== undefined && fromProcess.trim().length > 0) {
            return parseLogLevel(fromProcess);
        }
    }

    if (typeof import.meta !== 'undefined') {
        const env = (import.meta as ImportMeta & { env?: Record<string, string | undefined> }).env;
        const fromVite = env?.['VITE_LOG_LEVEL'];
        if (fromVite !== undefined && fromVite.trim().length > 0) {
            return parseLogLevel(fromVite);
        }
    }

    return 'error';
}

export function consoleLevelsFromMinLevel(
    minLevel: LogLevelName,
): Required<NonNullable<LoggerConfig['consoleLevels']>> {
    const minIndex = LOG_LEVEL_ORDER.indexOf(minLevel);

    return {
        trace: minIndex <= LOG_LEVEL_ORDER.indexOf('trace'),
        debug: minIndex <= LOG_LEVEL_ORDER.indexOf('debug'),
        info: minIndex <= LOG_LEVEL_ORDER.indexOf('info'),
        warn: minIndex <= LOG_LEVEL_ORDER.indexOf('warn'),
        error: minIndex <= LOG_LEVEL_ORDER.indexOf('error'),
        fatal: minIndex <= LOG_LEVEL_ORDER.indexOf('fatal'),
    };
}
